import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { MyContext } from '../Context/MyContext';
import { API_URL } from '../Api/api';
import './LanguageSelector.css';
import AppIcon from './AppIcon';

const FALLBACK_LANGUAGES = [
  { code: 'en', nativeName: 'English', name: 'English' },
  { code: 'ta', nativeName: 'தமிழ்', name: 'Tamil' },
  { code: 'hi', nativeName: 'हिन्दी', name: 'Hindi' },
  { code: 'te', nativeName: 'తెలుగు', name: 'Telugu' },
  { code: 'bn', nativeName: 'বাংলা', name: 'Bengali' },
  { code: 'mr', nativeName: 'मराठी', name: 'Marathi' },
  { code: 'gu', nativeName: 'ગુજરાતી', name: 'Gujarati' },
  { code: 'kn', nativeName: 'ಕನ್ನಡ', name: 'Kannada' },
  { code: 'ml', nativeName: 'മലയാളം', name: 'Malayalam' },
  { code: 'pa', nativeName: 'ਪੰਜਾਬੀ', name: 'Punjabi' },
  { code: 'or', nativeName: 'ଓଡ଼ିଆ', name: 'Odia' },
  { code: 'as', nativeName: 'অসমীয়া', name: 'Assamese' },
  { code: 'ur', nativeName: 'اردو', name: 'Urdu' },
];

const LanguageSelector = ({ variant = 'dropdown', className = '' }) => {
  const { locale, changeLocale, localizationEnabled } = useContext(MyContext);
  const [open, setOpen] = useState(false);
  const [languages, setLanguages] = useState(FALLBACK_LANGUAGES);

  useEffect(() => {
    axios.get(`${API_URL}/localization/languages`)
      .then((r) => {
        if (Array.isArray(r.data?.languages) && r.data.languages.length) {
          setLanguages(r.data.languages);
        }
      })
      .catch(() => {});
  }, []);

  const current = useMemo(
    () => languages.find((l) => l.code === locale) || { code: locale, nativeName: locale?.toUpperCase() },
    [languages, locale],
  );

  const handleSelect = async (code) => {
    setOpen(false);
    if (code === locale) return;
    await changeLocale(code);
    toast.success('Language updated. Database content will refresh.');
  };

  if (localizationEnabled === false) return null;

  if (variant === 'inline') {
    return (
      <div className={`lang-inline ${className}`}>
        <label className="lang-inline-label">Language</label>
        <select
          className="lang-inline-select"
          value={locale}
          onChange={(e) => handleSelect(e.target.value)}
        >
          {languages.map((l) => (
            <option key={l.code} value={l.code}>{l.nativeName || l.name}</option>
          ))}
        </select>
      </div>
    );
  }

  if (variant === 'header') {
    return (
      <div className={`lang-wrap lang-header ${className}`}>
        <button
          type="button"
          className={`lang-header-trigger${open ? ' lang-header-trigger--open' : ''}`}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={`Language: ${current.nativeName || current.name || locale}`}
        >
          <span className="lang-header-icon" aria-hidden>
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M2 12h20M12 2a15 15 0 010 20M12 2a15 15 0 000 20" />
            </svg>
          </span>
          <span className="lang-header-label">{current.nativeName || current.name || 'EN'}</span>
        </button>
        {open && (
          <>
            <div className="lang-backdrop" onClick={() => setOpen(false)} />
            <div className="lang-menu lang-header-menu" role="listbox">
              <p className="lang-menu-title">Language</p>
              {languages.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  className={`lang-option ${l.code === locale ? 'active' : ''}`}
                  onClick={() => handleSelect(l.code)}
                >
                  <span>{l.nativeName || l.name}</span>
                  {l.code === locale ? <span className="lang-check"><AppIcon name="check" size={14} /></span> : null}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  if (variant === 'floating') {
    return (
      <div className={`lang-wrap lang-floating ${className}`}>
        <button type="button" className="lang-trigger lang-floating-trigger" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M2 12h20M12 2a15 15 0 010 20M12 2a15 15 0 000 20" />
          </svg>
          <span>{current.nativeName || current.name || 'EN'}</span>
        </button>
        {open && (
          <>
            <div className="lang-backdrop" onClick={() => setOpen(false)} />
            <div className="lang-menu lang-floating-menu" role="listbox">
              <p className="lang-menu-title">Language</p>
              {languages.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  className={`lang-option ${l.code === locale ? 'active' : ''}`}
                  onClick={() => handleSelect(l.code)}
                >
                  <span>{l.nativeName || l.name}</span>
                  {l.code === locale ? <span className="lang-check"><AppIcon name="check" size={14} /></span> : null}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className={`lang-wrap ${className}`}>
      <button type="button" className="lang-trigger" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <path d="M2 12h20M12 2a15 15 0 010 20M12 2a15 15 0 000 20" />
        </svg>
        <span>{current.nativeName || current.name || 'EN'}</span>
      </button>
      {open && (
        <>
          <div className="lang-backdrop" onClick={() => setOpen(false)} />
          <div className="lang-menu" role="listbox">
            <p className="lang-menu-title">Select Language</p>
            {languages.map((l) => (
              <button
                key={l.code}
                type="button"
                className={`lang-option ${l.code === locale ? 'active' : ''}`}
                onClick={() => handleSelect(l.code)}
              >
                <span>{l.nativeName || l.name}</span>
                {l.code === locale ? <span className="lang-check"><AppIcon name="check" size={14} /></span> : null}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default LanguageSelector;
